import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, MessageCircle, Phone, X, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { api } from "@/lib/api";
import { useLang, localizeEnum } from "@/context/LanguageContext";

const SPECS = [
  { key: "anno", it: "Anno", en: "Year", format: (v) => v },
  { key: "km", it: "Chilometri", en: "Mileage", format: (v) => `${Number(v).toLocaleString("it-IT")} km` },
  { key: "cv", it: "Potenza", en: "Power", format: (v) => `${v} CV` },
  { key: "cilindrata", it: "Cilindrata", en: "Displacement", format: (v) => `${Number(v).toLocaleString("it-IT")} cc` },
  { key: "alimentazione", it: "Alimentazione", en: "Fuel", enum: true, format: (v) => v },
  { key: "cambio", it: "Cambio", en: "Gearbox", enum: true, format: (v) => v },
  { key: "cilindri", it: "Cilindri", en: "Cylinders", format: (v) => v },
  { key: "trazione", it: "Trazione", en: "Drivetrain", enum: true, format: (v) => v },
  { key: "colore", it: "Colore", en: "Colour", format: (v) => v },
];

export default function CarDetailPage() {
  const { id } = useParams();
  const { lang } = useLang();
  const [car, setCar] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    api.get(`/cars/${id}?lang=${lang}`)
      .then((r) => setCar(r.data))
      .catch(() => setNotFound(true));
  }, [id, lang]);

  const totalPhotos = car?.foto?.length || 0;
  const openLightbox = useCallback((idx) => {
    setActiveImg(idx);
    setLightboxOpen(true);
  }, []);
  const closeLightbox = useCallback(() => setLightboxOpen(false), []);
  const prevPhoto = useCallback(() => setActiveImg((i) => (i - 1 + totalPhotos) % totalPhotos), [totalPhotos]);
  const nextPhoto = useCallback(() => setActiveImg((i) => (i + 1) % totalPhotos), [totalPhotos]);

  // Blocca scroll body + tastiera (Esc, frecce) quando la lightbox è aperta
  useEffect(() => {
    if (!lightboxOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") prevPhoto();
      else if (e.key === "ArrowRight") nextPhoto();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [lightboxOpen, closeLightbox, prevPhoto, nextPhoto]);

  if (notFound) {
    return (
      <div className="pt-40 pb-32 px-6 max-w-[1200px] mx-auto text-center" data-testid="car-not-found">
        <div className="font-serif text-5xl text-white mb-6">{lang === "en" ? "Car not found" : "Vettura non trovata"}</div>
        <Link to="/vetture" className="btn-magnetic">{lang === "en" ? "Back to catalogue" : "Torna al catalogo"}</Link>
      </div>
    );
  }

  if (!car) {
    return <div className="pt-40 px-6 max-w-[1600px] mx-auto"><div className="h-[600px] bg-neutral-900/60 animate-pulse" /></div>;
  }

  const cover = car.foto?.[activeImg] || car.foto?.[0] || "https://images.pexels.com/photos/38570/lamborghini-car-speed-prestige-38570.jpeg";
  const isNegotiation = car.prezzo === 0;
  const priceText = isNegotiation
    ? (lang === "en" ? "Price on request" : "Trattativa Riservata")
    : car.prezzo != null
      ? `€ ${Number(car.prezzo).toLocaleString("it-IT")}`
      : (lang === "en" ? "Price on request" : "Prezzo su richiesta");
  const waMsg = encodeURIComponent(`Ciao Davide, sono interessato alla ${car.marca} ${car.modello} (${car.anno || ""}).`);

  return (
    <div className="pt-32 pb-32 px-6 sm:px-10 lg:px-14 max-w-[1600px] mx-auto" data-testid="car-detail-page">
      <Link to="/vetture" className="inline-flex items-center gap-2 label-eyebrow text-neutral-500 hover:text-white transition-colors" data-testid="back-to-cars">
        <ArrowLeft size={14} /> {lang === "en" ? "Back to catalogue" : "Torna al catalogo"}
      </Link>

      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9 }}
        className="mt-8 font-sans text-4xl sm:text-6xl lg:text-8xl font-light leading-[0.95] tracking-tight text-white"
        data-testid="car-title"
      >
        {car.marca} <span className="text-neutral-500">{car.modello}</span>
      </motion.h1>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            className="car-frame aspect-[16/10] bg-neutral-900 border border-white/5 cursor-zoom-in relative group"
            onContextMenu={(e) => e.preventDefault()}
            onClick={() => openLightbox(activeImg)}
            role="button"
            aria-label={lang === "en" ? "Enlarge photo" : "Ingrandisci foto"}
            data-testid="car-main-photo"
          >
            <img
              key={cover}
              src={cover}
              alt={`${car.marca} ${car.modello}`}
              className="w-full h-full object-contain"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
            />
            <div className="absolute bottom-4 right-4 bg-black/70 border border-white/10 backdrop-blur-md p-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <Maximize2 size={16} className="text-white" />
            </div>
          </motion.div>
          {car.foto && car.foto.length > 1 && (
            <div className="mt-4 grid grid-cols-4 sm:grid-cols-6 gap-3" data-testid="gallery-thumbs">
              {car.foto.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  onDoubleClick={() => openLightbox(i)}
                  onContextMenu={(e) => e.preventDefault()}
                  className={`car-frame aspect-[4/3] overflow-hidden border transition-colors bg-neutral-900 ${
                    i === activeImg ? "border-white" : "border-white/10 hover:border-white/40"
                  }`}
                  data-testid={`gallery-thumb-${i}`}
                  title={lang === "en" ? "Double click to enlarge" : "Doppio click per ingrandire"}
                >
                  <img
                    src={src}
                    alt=""
                    className="w-full h-full object-contain"
                    draggable={false}
                    onContextMenu={(e) => e.preventDefault()}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        <aside className="lg:col-span-4 space-y-10">
          <div className="border border-white/10 p-8">
            {car.venduto ? (
              <>
                <div className="label-eyebrow text-neutral-500 mb-2">{lang === "en" ? "Status" : "Stato"}</div>
                <div className="font-sans font-light text-4xl text-white" data-testid="car-price">{lang === "en" ? "Sold" : "Venduta"}</div>
                <div className="mt-3 label-eyebrow text-neutral-500">{lang === "en" ? "This car has been sold" : "Questa vettura è stata venduta"}</div>
              </>
            ) : (
              <>
                <div className="label-eyebrow text-neutral-500 mb-2">
                  {isNegotiation ? (lang === "en" ? "Availability" : "Disponibilità") : (lang === "en" ? "Price" : "Prezzo")}
                </div>
                <div
                  className={`text-white ${isNegotiation ? "font-sans font-medium text-2xl sm:text-3xl tracking-tight" : "font-sans font-light text-4xl"}`}
                  data-testid="car-price"
                >
                  {priceText}
                </div>
                {isNegotiation && (
                  <div className="mt-3 label-eyebrow text-neutral-500">{lang === "en" ? "Contact us for details" : "Contattaci per i dettagli"}</div>
                )}
              </>
            )}
            <div className="mt-8 space-y-3">
              <a
                href={`https://wa.me/393534520559?text=${waMsg}`}
                target="_blank" rel="noopener noreferrer"
                className="btn-magnetic btn-solid w-full justify-center"
                data-testid="car-cta-whatsapp"
              >
                <MessageCircle size={16} /> WhatsApp
              </a>
              <a href="tel:+393534520559" className="btn-magnetic w-full justify-center whitespace-nowrap" data-testid="car-cta-phone">
                <Phone size={16} /> {lang === "en" ? "Call Davide" : "Chiama Davide"} <span className="tabular-nums font-medium">+39 353 452 0559</span>
              </a>
            </div>
          </div>

          <div>
            <div className="label-eyebrow text-neutral-500 mb-6">{lang === "en" ? "Technical details" : "Scheda tecnica"}</div>
            <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10">
              {SPECS.map((spec) => {
                const val = car[spec.key];
                if (val == null || val === "") return null;
                const shown = spec.enum ? localizeEnum(lang, spec.format(val)) : spec.format(val);
                return (
                  <div key={spec.key} className="bg-[#0a0a0a] p-5" data-testid={`spec-${spec.key}`}>
                    <div className="label-eyebrow text-neutral-600 mb-2">{lang === "en" ? spec.en : spec.it}</div>
                    <div className="font-sans font-light text-xl text-white">{shown}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </aside>
      </div>

      {car.descrizione && (
        <div className="mt-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-3 label-eyebrow text-neutral-500">{lang === "en" ? "Description" : "Descrizione"}</div>
          <div className="lg:col-span-8 text-lg sm:text-xl font-extralight leading-relaxed text-neutral-300 whitespace-pre-wrap tracking-wide">
            {car.descrizione}
          </div>
        </div>
      )}

      <AnimatePresence>
        {lightboxOpen && totalPhotos > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex items-center justify-center"
            onClick={closeLightbox}
            onContextMenu={(e) => e.preventDefault()}
            data-testid="car-lightbox"
          >
            {/* Chiudi */}
            <button
              onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
              className="absolute top-6 right-6 p-3 border border-white/10 hover:border-white/60 hover:bg-white/10 transition-colors"
              aria-label={lang === "en" ? "Close" : "Chiudi"}
              data-testid="lightbox-close"
            >
              <X size={22} className="text-white" />
            </button>

            {/* Indice foto */}
            <div className="absolute top-6 left-6 label-eyebrow text-neutral-300 border border-white/10 px-3 py-2 bg-black/40">
              {activeImg + 1} / {totalPhotos}
            </div>

            {/* Precedente */}
            {totalPhotos > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
                className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 p-4 border border-white/10 hover:border-white/60 hover:bg-white/10 transition-colors"
                aria-label={lang === "en" ? "Previous photo" : "Foto precedente"}
                data-testid="lightbox-prev"
              >
                <ChevronLeft size={26} className="text-white" />
              </button>
            )}

            {/* Immagine */}
            <motion.img
              key={activeImg}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35 }}
              src={car.foto[activeImg]}
              alt={`${car.marca} ${car.modello} — ${activeImg + 1}`}
              className="max-w-[92vw] max-h-[88vh] object-contain select-none"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              onClick={(e) => e.stopPropagation()}
              data-testid="lightbox-image"
            />

            {/* Successiva */}
            {totalPhotos > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
                className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 p-4 border border-white/10 hover:border-white/60 hover:bg-white/10 transition-colors"
                aria-label={lang === "en" ? "Next photo" : "Foto successiva"}
                data-testid="lightbox-next"
              >
                <ChevronRight size={26} className="text-white" />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
